'use client';

import { Root, Trigger, Content, Arrow } from '@radix-ui/react-hover-card';
import { useEffect, useRef } from 'react';
import { HoverCardProps } from './HoverCard.types';
import { hoverCard } from './HoverCard.styles';
import { cx } from '@onbeam/business-styled-system/css';

export const HoverCard = ({
  children,
  open,
  onOpenChange,
  trigger,
  title,
  align = 'center',
  side = 'bottom',
  className,
  onInteractOutside,
  ...props
}: HoverCardProps) => {
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const rootRef = useRef<HTMLElement>(null);
  const classes = hoverCard();

  useEffect(() => {
    if (!triggerRef.current) return;
    const rootNode = triggerRef.current.getRootNode();
    // When inside shadow dom, place the card in the shadow root instead of the document body
    if (rootNode.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
      rootRef.current = triggerRef.current.getRootNode() as HTMLElement;
    }
  }, []);

  return (
    <Root
      open={open}
      onOpenChange={onOpenChange}
      openDelay={0}
      closeDelay={100}
    >
      <Trigger ref={triggerRef} asChild>
        {trigger ?? <div aria-hidden className={classes.hidden} />}
      </Trigger>
      <Content
        sideOffset={8}
        data-hovercard-content
        align={align}
        side={side}
        {...props}
        className={cx(classes.content, className)}
        // Radix already handles this logic, but not for triggers within the Shadow DOM
        // Using `composedPath` we can find the trigger within the Shadow DOM
        onInteractOutside={(e) => {
          const path = e.detail.originalEvent.composedPath();
          const targetIsTrigger = path.some(
            (el) => el instanceof Node && triggerRef.current?.contains(el),
          );
          if (targetIsTrigger) e.preventDefault();
          onInteractOutside?.(e);
        }}
        {...props}
      >
        {children}
        <Arrow
          className={classes.arrow}
          strokeWidth={2}
          height={8}
          width={16}
        />
      </Content>
    </Root>
  );
};
